import './MessageList.scss';

import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { List } from 'immutable';
import { throttle } from 'lodash';
import PropTypes from 'prop-types';

import Loader from 'components/Loader';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageItem from 'components/messenger/MessageItem';
import Text from 'components/Text';

import cxHelpers from 'util/className';
import { toI18n } from 'util/i18n';

const THROTTLE_WAIT = 16;

@cxHelpers('MessageList')
export default class MessageList extends PureComponent {
  static propTypes = {
    messages: PropTypes.instanceOf(List),
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customEmptyMessage: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    isFetchingMore: PropTypes.bool.isRequired,
    currentUserCanDeleteMessages: PropTypes.bool,
    onFetchMore: PropTypes.func.isRequired,
    onShowReadList: PropTypes.func,
    onDeleteMessage: PropTypes.func,
  };

  componentDidMount() {
    this.scrollToBottom();
    this.bindScrollListener();
  }

  componentDidUpdate(prevProps) {
    this.autoscrollIfNecessary(prevProps);
  }

  componentWillUnmount() {
    this.unbindScrollListener();
  }

  autoscrollIfNecessary = prevProps => {
    if (!this.props.messages || !this.props.messages.size) {
      return;
    }

    const newData = prevProps.messages.size !== this.props.messages.size;
    const scrolledToTop = this.container.scrollTop === 0;

    const scrollForNewData = newData && !scrolledToTop;
    const scrollForChannelChange = this.props.channelId !== prevProps.channelId;
    const scrollForSentMessage =
      newData && this.props.messages.last().get('mine');

    if (scrollForNewData || scrollForChannelChange || scrollForSentMessage) {
      this.scrollToBottom();
    }
  };

  getContainerRef = container => {
    this.container = container;
  };

  // setTimeout gives images breathing room to expand their containers' dimensions
  // before scrollHeight is read
  scrollToBottom = () =>
    setTimeout(() => {
      if (this.container) {
        this.container.scrollTop = this.container.scrollHeight;
      }
    }, 10);

  handleScroll = () => {
    if (this.container.scrollTop === 0 && !this.props.isFetchingMore) {
      this.props.onFetchMore(this.props.messages.getIn([0, 'id']));
    }
  };

  throttledHandleScroll = throttle(this.handleScroll, THROTTLE_WAIT);

  bindScrollListener = () => {
    findDOMNode(this.container).addEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  };

  unbindScrollListener = () => {
    findDOMNode(this.container).removeEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  };

  render() {
    const {
      messages,
      customEmptyMessage,
      isLoading,
      isFetchingMore,
      onShowReadList,
      onDeleteMessage,
      currentUserCanDeleteMessages,
    } = this.props;

    return (
      <div className={this.cx()} ref={this.getContainerRef}>
        <LoadingIndicator
          type="threeDots"
          centered
          className={this.cxEl('loading-more', { show: isFetchingMore })}
        />
        <Loader
          type="threeDots"
          centeredRelative
          isLoading={isLoading}
          onTransitionEnd={this.scrollToBottom}
        >
          <div className={this.cxEl('list')}>
            {messages && messages.size > 0 ? (
              messages.map((message, i) => (
                <MessageItem
                  key={i}
                  message={message}
                  onShowReadList={onShowReadList}
                  onDeleteMessage={onDeleteMessage}
                  currentUserCanDeleteMessages={currentUserCanDeleteMessages}
                />
              ))
            ) : (
              <div className={this.cxEl('empty')}>
                <Text italic asHTML>
                  {customEmptyMessage || toI18n('messenger.no_messages')}
                </Text>
              </div>
            )}
          </div>
        </Loader>
      </div>
    );
  }
}
